import { formatToCurrency } from '@farmshare/utils';
import { ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useState } from 'react';

import { Cutsheet, EnumCutsheetType } from 'lib/graphql';

import { CutsheetTooltipContent } from 'features/cutsheets/components/cutsheet-tooltip';

interface CutsheetCardProps {
  cutsheet: Cutsheet;
  isSelected: boolean;
  onSelect: () => void;
  shopName?: string;
}

export function CutsheetCard({
  cutsheet,
  isSelected,
  onSelect,
  shopName,
}: CutsheetCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const isHouse = cutsheet.type === EnumCutsheetType.Vendor;
  let subtitle: string | undefined;
  if (isHouse && cutsheet.pricePerPound) {
    subtitle = `${shopName || 'House'} recommended · ${formatToCurrency(cutsheet.pricePerPound)}/lb`;
  } else if (isHouse) {
    subtitle = `${shopName || 'House'} recommended`;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        borderColor: isSelected ? 'primary.main' : undefined,
        borderWidth: isSelected ? 2 : 1,
        bgcolor: isSelected ? 'transparent' : undefined,
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              noWrap
              sx={{ lineHeight: 1.3, fontSize: '1.09rem' }}
            >
              {cutsheet.name}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.2, fontSize: '0.9375rem' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          {isSelected ? (
            <Chip
              label="Current"
              size="small"
              color="primary"
              sx={{ flexShrink: 0, mt: 0.25 }}
            />
          ) : (
            <Button
              variant="outlined"
              size="medium"
              onClick={onSelect}
              sx={{ flexShrink: 0, textTransform: 'none' }}
            >
              Select
            </Button>
          )}
        </Stack>

        {cutsheet.lastUsedDate && (
          <Box sx={{ mt: 1 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.9375rem',
              }}
            >
              Last used
            </Typography>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{ fontSize: '1.09rem' }}
            >
              {DateTime.fromISO(cutsheet.lastUsedDate).toFormat('MMM d, yyyy')}
            </Typography>
          </Box>
        )}
      </CardContent>

      <Box sx={{ width: '100%' }}>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            py: 0.5,
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={() => setDetailsOpen((prev) => !prev)}
        >
          <Typography
            variant="caption"
            color="primary"
            sx={{ fontWeight: 500, fontSize: '0.9375rem' }}
          >
            Details
          </Typography>
          <ExpandMore
            fontSize="small"
            color="primary"
            sx={{
              ml: 0.25,
              transition: 'transform 0.2s',
              transform: detailsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
        <Collapse in={detailsOpen}>
          <Divider />
          <Box sx={{ px: 1.5, py: 1 }}>
            <CutsheetTooltipContent cutsheet={cutsheet} showName={false} />
          </Box>
        </Collapse>
      </Box>
    </Card>
  );
}
